// The WhatsApp inbox. Staff read and answer messages from applicants, drivers
// and owners.
//
// Every fact on this screen comes from Postgres: who a number belongs to,
// whether the 24 hour window is open and when it closes, what is unhandled. The
// page arranges them. CLAUDE.md rule 4.
//
// REFRESHES EVERY 15 SECONDS rather than over realtime. Nothing else in the
// cockpit uses Supabase realtime, and the tables behind this are granted to
// nobody in the browser, so realtime would mean inventing a second way in. A
// hidden tab does not poll; coming back to it refreshes at once.
//
// On a narrow screen the list and the thread stack: the list first, and the
// thread with a Back button once a conversation is chosen. The chosen number
// is kept in the address (?phone=), so a link to a conversation opens it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, ExternalLink, MessageCircle, ShieldAlert, Timer } from 'lucide-react'
import {
  useGetConversations,
  useGetInboxAccess,
  useGetThread,
  useMarkHandled,
} from '../hooks/backend/whatsapp'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { ConversationList, type InboxFilter } from '../components/whatsapp/ConversationList'
import { ThreadView } from '../components/whatsapp/ThreadView'
import { Composer } from '../components/whatsapp/Composer'
import {
  formatPhone,
  kindLabel,
  kindTone,
  windowLeft,
  type Conversation,
  type ThreadMessage,
} from '../utils/whatsapp'

const REFRESH_MS = 15_000

type ConversationsResult = { conversations: Conversation[]; as_at: string }
type ThreadResult = {
  phone: string
  conversation: Conversation | null
  messages: ThreadMessage[]
  as_at: string
}

/** Calls fn every REFRESH_MS while the tab is visible, and once on becoming visible. */
function usePoll(fn: () => void, enabled: boolean) {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (!enabled) return
    const tick = () => {
      if (document.visibilityState === 'visible') ref.current()
    }
    const id = window.setInterval(tick, REFRESH_MS)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [enabled])
}

export default function WhatsApp() {
  const accessQuery = useGetInboxAccess<{ allowed: boolean }>()
  const conversationsQuery = useGetConversations<ConversationsResult>()
  const threadQuery = useGetThread<ThreadResult>()
  const markHandled = useMarkHandled<{ marked: number }>()

  const [params, setParams] = useSearchParams()
  const selected = params.get('phone')
  const [filter, setFilter] = useState<InboxFilter>('open')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [, setNow] = useState(() => Date.now())

  const loadAccess = accessQuery.trigger
  const loadConversations = conversationsQuery.trigger
  const loadThread = threadQuery.trigger

  useEffect(() => {
    void loadAccess({}, { skipCache: true }).catch(() => undefined)
  }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(search.trim()), 300)
    return () => window.clearTimeout(id)
  }, [search])

  const refreshList = useCallback(() => {
    void loadConversations({ filter, search: debounced || null }, { skipCache: true }).catch(() => undefined)
  }, [loadConversations, filter, debounced])

  const refreshThread = useCallback(() => {
    if (!selected) return
    void loadThread({ phone: selected }, { skipCache: true }).catch(() => undefined)
  }, [loadThread, selected])

  useEffect(() => {
    if (allowed) refreshList()
  }, [allowed, refreshList])
  useEffect(() => {
    if (allowed) refreshThread()
    setNotice(null)
  }, [allowed, refreshThread])

  usePoll(() => {
    refreshList()
    refreshThread()
    // Re-render so the "hours left" timers move even when nothing arrives.
    setNow(Date.now())
  }, allowed)

  const select = (phone: string | null) => {
    const next = new URLSearchParams(params)
    if (phone) next.set('phone', phone)
    else next.delete('phone')
    setParams(next, { replace: false })
  }

  const rows = conversationsQuery.data?.conversations ?? []
  // Only show the thread that was asked for. A response for the previous
  // number arriving late must not paint over the one just chosen.
  const thread =
    threadQuery.data && threadQuery.data.phone === selected?.replace(/\D/g, '') ? threadQuery.data : null
  const header: Conversation | null = useMemo(
    () => thread?.conversation ?? rows.find((r) => r.phone === selected) ?? null,
    [thread, rows, selected],
  )
  const w = header ? windowLeft(header) : { open: false, label: 'Closed' }

  const onMarkHandled = async () => {
    if (!selected) return
    setNotice(null)
    try {
      const res = await markHandled.trigger({ phone: selected })
      setNotice(
        res?.marked
          ? `Marked ${res.marked} message${res.marked === 1 ? '' : 's'} handled.`
          : 'Nothing was waiting in this conversation.',
      )
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e))
    }
    refreshList()
    refreshThread()
  }

  const onSent = () => {
    refreshThread()
    refreshList()
  }

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}
          >
            Try again
          </Button>
        </div>
      </div>
    )
  }

  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The WhatsApp inbox is open to the people who answer Fleetly’s WhatsApp messages.
          </p>
          <p className="text-xs text-muted-foreground">
            If you should be one of them, ask an admin to switch on can_use_whatsapp_inbox for you in cockpit
            access.
          </p>
        </div>
      </div>
    )
  }

  const openCount = filter === 'open' ? rows.length : null

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <MessageCircle className="h-5 w-5" />
        </div>
        <h1 className="text-lg font-semibold text-foreground">WhatsApp</h1>
        {openCount !== null && openCount > 0 && (
          <Chip tone="brand">
            {openCount}
            <span className="ml-1 opacity-70">open</span>
          </Chip>
        )}
      </div>

      {conversationsQuery.error && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {conversationsQuery.error}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside
          className={cn(
            'min-h-0 w-full border-r border-border md:w-80 md:shrink-0 lg:w-96',
            selected ? 'hidden md:block' : 'block',
          )}
        >
          <ConversationList
            rows={rows}
            loading={conversationsQuery.loading}
            loaded={conversationsQuery.data !== null}
            filter={filter}
            onFilter={setFilter}
            search={search}
            onSearch={setSearch}
            selected={selected}
            onSelect={(p) => select(p)}
          />
        </aside>

        <section className={cn('min-h-0 min-w-0 flex-1 flex-col', selected ? 'flex' : 'hidden md:flex')}>
          {!selected && (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
              Choose a conversation on the left.
            </div>
          )}

          {selected && (
            <>
              {/* Two rows on a phone (who, then what you can do), one row from
                  md up. A single wrapping row squeezed the name to nothing at
                  375px, which is the one thing the header exists to show. */}
              <div className="flex flex-col gap-2 border-b border-border px-3 py-2 md:flex-row md:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => select(null)}
                    className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {header?.display_name ?? formatPhone(selected)}
                    </p>
                    <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                      <span className="tabular-nums">{formatPhone(selected.replace(/\D/g, ''))}</span>
                      {header?.profile_name && header.profile_name !== header.display_name && (
                        <span>WhatsApp name: {header.profile_name}</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {header && (
                    <Chip tone={kindTone(header.contact_kind)}>{kindLabel(header.contact_kind)}</Chip>
                  )}
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 text-xs tabular-nums',
                      w.open ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground',
                    )}
                  >
                    <Timer className="h-3.5 w-3.5" /> {w.open ? w.label : 'Window closed'}
                  </span>
                  {header?.record_route ? (
                    <Button asChild size="sm" variant="outline" className="h-8">
                      <Link to={header.record_route}>
                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open record
                      </Link>
                    </Button>
                  ) : (
                    <span
                      className="text-[11px] text-muted-foreground"
                      title="This number is not matched to anybody on file. It is matched on people.whatsapp_phone or people.primary_phone."
                    >
                      Not on file
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="brand"
                    className="h-8"
                    onClick={() => void onMarkHandled()}
                    disabled={markHandled.loading || !header || header.unread_count === 0}
                  >
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark handled
                  </Button>
                </div>
              </div>

              {notice && (
                <div className="border-b border-border bg-muted/50 px-4 py-1.5 text-xs text-muted-foreground">
                  {notice}
                </div>
              )}
              {threadQuery.error && (
                <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
                  {threadQuery.error}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto bg-muted/30">
                {!thread && threadQuery.loading && (
                  <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
                    <Spinner className="h-4 w-4" /> Reading the conversation…
                  </div>
                )}
                {thread && <ThreadView messages={thread.messages} />}
              </div>

              {/* Not until the header has loaded: before that the window reads as
                  closed, and the template picker would flash up and fetch
                  templates for a conversation that may well be open. */}
              {header && <Composer phone={selected.replace(/\D/g, '')} windowOpen={w.open} onSent={onSent} />}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
